import { Head, Link } from '@inertiajs/react';
import PublicLayout from '@/layouts/public-layout';
import { Book, PaginatedData } from '@/types/models';
import { BookOpen } from 'lucide-react';
import { BookCard } from '@/components/book-card';
import { SearchFilter } from '@/components/search-filter';
import { useBookFilters } from '@/hooks/use-book-filters';
import { useTranslations } from '@/hooks/use-translations';
import { SeoHead } from '@/components/seo-head';
import { Pagination } from '@/components/public/pagination';

interface Genre {
    id: string;
    name: string;
    slug: string;
    books_count: number;
}

interface Author {
    id: string;
    name: string;
}

interface Props {
    books: PaginatedData<Book>;
    genres: Genre[];
    authors: Author[];
    search: string;
    sort: string;
    author_id: string | null;
    locale: string;
}

export default function Home({ books, genres, authors, search: initialSearch, sort: initialSort, author_id: initialAuthorId, locale }: Props) {
    const { t } = useTranslations();
    
    // Use the book filters hook
    const {
        filteredBooks,
        search,
        sort,
        authorId,
        genreId,
        handleSearch,
        handleSort,
        handleAuthorFilter,
        handleGenreFilter
    } = useBookFilters({
        books: books.data,
        initialSearch,
        initialSort,
        initialAuthorId
    });

    // Get the base URL from props or environment
    const baseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';
    
    // Generate structured data for website
    const websiteJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": import.meta.env.VITE_APP_NAME || 'Brajti',
        "url": baseUrl,
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/${locale}?search={search_term_string}`
            },
            "query-input": "required name=search_term_string"
        }
    };

    return (
        <PublicLayout>
            <SeoHead
                title={t('Browse Books')}
                description={t('Discover amazing books from various genres and authors. Browse our collection of literature.')}
                jsonLd={websiteJsonLd}
            />



            {/* Books Grid */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    {/* Title and Filters Row */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
                        <h2 className="text-xl font-semibold flex-shrink-0">{t('All Books')}</h2>
                        
                        {/* Search and Filter */}
                        <div className="flex-1">
                            <SearchFilter
                                onSearch={handleSearch}
                                onSort={handleSort}
                                onAuthorFilter={handleAuthorFilter}
                                onGenreFilter={handleGenreFilter}
                                authors={authors}
                                genres={genres}
                                initialSearch={search}
                                initialSort={sort}
                                initialAuthorId={authorId}
                                initialGenreId={genreId}
                            />
                        </div>
                    </div>
                    {filteredBooks.length === 0 ? (
                        <div className="text-center py-16">
                            <BookOpen className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                            <h2 className="text-2xl font-semibold mb-2">{t('No books found')}</h2>
                            <p className="text-muted-foreground">
                                {search || authorId || genreId ? t('No books match your filters') : t('No books have been added yet')}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {filteredBooks.map((book) => (
                                    <BookCard key={book.id} book={book} translatedSlug={book.translated_slug} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {books.links && books.links.length > 3 && (
                                <Pagination
                                    links={books.links.map(link => ({
                                        ...link,
                                        url: link.url && !link.url.includes('/' + locale) 
                                            ? (() => {
                                                const url = new URL(link.url);
                                                url.pathname = `/${locale}${url.pathname}`;
                                                return url.toString();
                                            })()
                                            : link.url
                                    }))}
                                    currentPage={books.current_page}
                                    lastPage={books.last_page}
                                    className="mt-8"
                                />
                            )}
                        </>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}